import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, signal } from '@angular/core'
import { FormGroup, ReactiveFormsModule } from '@angular/forms'
import { CommonModule } from '@angular/common'
import { InsuredBaseForm } from '@features/insured/components/insured-base-form.directive'
import { distinctUntilChanged, switchMap } from 'rxjs'
import { AlbertInputComponent } from '@shared/components/albert-input/albert-input.component'
import { AlbertOverrideWrapper } from '@shared/components/albert-override-wrapper/albert-override-wrapper'
import { AlbertCheckboxComponent } from '@shared/components/albert-checkbox/albert-checkbox.component'
import { ReinsuranceOrderForm } from '@features/insured/models/coverages/base-coverage.model'
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop'
import { AlbertTextareaComponent } from '@shared/components/albert-textarea/albert-textarea.component'
import { AlbertInputnumberComponent } from '@shared/components/albert-inputnumber/albert-inputnumber.component'
import { AlbertSelectComponent } from '@shared/components/albert-select/albert-select.component'
import { MemberService } from '@features/insured/services/member/member.service'
import { hasActualValue, valueWithChanges } from '@core/utils/form-utils'

interface FrontingCompanyOption {
  memberId: string;
  company: string;
}

/** Always selectable, whatever the backend returns for the active filter. */
const NONE_FRONTING_COMPANY: FrontingCompanyOption = { memberId: 'null', company: 'None' }

/**
 * Reinsurance order of a sub-coverage: share, fronting company (with "None" as option) and additional
 * order information, each with Origami value and override.
 *
 * @example
 * <albert-reinsurance-form [form]="subCoverage.controls.reinsurance" [isCreateMode]="false" />
 */
@Component({
  selector: 'albert-reinsurance-form',
  imports: [
    ReactiveFormsModule, CommonModule, AlbertInputComponent, AlbertOverrideWrapper, AlbertCheckboxComponent, AlbertTextareaComponent,
    AlbertInputnumberComponent, AlbertSelectComponent
  ],
  templateUrl: './reinsurance-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class ReinsuranceFormComponent extends InsuredBaseForm<ReinsuranceOrderForm> implements OnInit {
  /** Reinsurance order form group of the sub-coverage */
  public override readonly form = input.required<FormGroup<ReinsuranceOrderForm>>()
  /** Whether the additional order information input is shown */
  public readonly hasAdditionalInfoChecked = signal(false)
  private readonly memberService = inject(MemberService)
  private readonly fetchedFrontingCompanies = signal<FrontingCompanyOption[]>([])
  // Follows the stored fronting company. switchMap drops the previous form's subscription when the form input
  // changes, and toSignal unsubscribes when the component is destroyed, so it never outlives its form.
  private readonly selectedFrontingCompany = toSignal(
    toObservable(this.form).pipe(switchMap(form => valueWithChanges(form.controls.frontingCompany.controls.localValue))),
    { initialValue: null }
  )
  /**
   * Fronting company options: "None" first, then the stored value if the backend did not return it
   * for the active filter (so it stays selectable), then the fetched companies.
   */
  public readonly frontingCompanyOptions = computed<FrontingCompanyOption[]>(() => {
    const fetched = this.fetchedFrontingCompanies()
    const selected = this.selectedFrontingCompany()
    const keepSelected = hasActualValue(selected)
      && selected !== NONE_FRONTING_COMPANY.company
      && !fetched.some(option => option.company === selected)
    return [
      NONE_FRONTING_COMPANY,
      ...(keepSelected ? [{ memberId: selected, company: selected }] : []),
      ...fetched
    ]
  })

  /** Couples the additional information checkbox with its input. */
  public ngOnInit(): void {
    const form = this.form()

    this.setupToggleLogic(form.controls.additionalInfoContent, this.hasAdditionalInfoChecked)

    form.controls.additionalInfoContent.controls.checked?.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      distinctUntilChanged()
    ).subscribe(val => {
      this.hasAdditionalInfoChecked.set(val ?? false)
    })
  }

  /**
   * Loads the fronting company options when the dropdown opens.
   *
   * Options are loaded when the dropdown opens rather than on init: every sub-coverage renders its own
   * reinsurance form, so eager loading fired one identical request per sub-coverage on each page load.
   * The stored value stays displayable without them via `frontingCompanyOptions`.
   */
  public onFrontingCompanyShow(): void {
    this.loadFrontingCompanies()
  }

  /**
   * Reloads the fronting company options for the text typed into the dropdown's filter.
   *
   * @param event - PrimeNG filter event carrying the typed text in `filter`
   */
  public onFrontingCompanyFilter(event: unknown): void {
    const typedEvent = event as { filter: string }
    this.loadFrontingCompanies(typedEvent.filter)
  }

  protected override getFormGroup(): FormGroup<ReinsuranceOrderForm> {
    return this.form()
  }

  private loadFrontingCompanies(query?: string): void {
    const category = this.categoryService.activeCertificateCategory()
    if (category === null) {
      return
    }
    const q = hasActualValue(query) ? query : ''
    // HTTP request: completes after one response, so the subscription needs no teardown.
    this.memberService.getParentCompanies(category, q).subscribe(response => {
      this.fetchedFrontingCompanies.set(response.map(item => ({ memberId: item.uuid, company: item.name })))
    })
  }
}
